import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/api';

const FantasyDraftManagement = () => {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fantasyDraft, setFantasyDraft] = useState(null);
  const [participatingTeams, setParticipatingTeams] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [currentPick, setCurrentPick] = useState(null);
  const [draftPicks, setDraftPicks] = useState([]);
  const [teamRosters, setTeamRosters] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [gmAssignments, setGmAssignments] = useState({});
  const [view, setView] = useState('setup'); // 'setup', 'draft', 'rosters'
  const [draftOrder, setDraftOrder] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [allTeams, setAllTeams] = useState([]);

  useEffect(() => {
    fetchFantasyDraft();
    fetchAvailableUsers();
    fetchAllTeams();
  }, [seasonId]);

  useEffect(() => {
    if (fantasyDraft && fantasyDraft.status !== 'not_started') {
      fetchCurrentPick();
      fetchAvailablePlayers();
      fetchDraftPicks();
      if (fantasyDraft.status === 'completed') {
        fetchTeamRosters();
      }
      
      // Auto-refresh during active draft
      let interval;
      if (fantasyDraft.status === 'in_progress') {
        interval = setInterval(() => {
          fetchCurrentPick();
          fetchAvailablePlayers();
          fetchDraftPicks();
          fetchFantasyDraft();
        }, 5000); // Refresh every 5 seconds during active draft
      }
      
      return () => {
        if (interval) clearInterval(interval);
      };
    }

    // Fetch draft order for setup phase
    if (fantasyDraft && fantasyDraft.status === 'not_started') {
      fetchDraftOrder();
    }
  }, [fantasyDraft]);

  const fetchFantasyDraft = async () => {
    try {
      const response = await api.get(`/fantasy-draft/season/${seasonId}`);
      setFantasyDraft(response.data.fantasyDraft);
      setParticipatingTeams(response.data.participatingTeams || []);
      
      // Initialize GM assignments if they exist, but validate user IDs
      const gmAssignmentsObj = {};
      if (response.data.participatingTeams) {
        for (const team of response.data.participatingTeams) {
          if (team.owner && team.owner.id) {
            // Only add valid user assignments
            gmAssignmentsObj[team.id] = team.owner.id;
          }
        }
      }
      setGmAssignments(gmAssignmentsObj);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching fantasy draft:', error);
      if (error.response?.status === 404) {
        // No fantasy draft exists yet
        setFantasyDraft(null);
        setParticipatingTeams([]);
        setGmAssignments({}); // Clear any stale assignments
        setLoading(false);
      }
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await api.get('/users');
      // For fantasy draft GM assignment, show all active users
      const users = response.data.filter(user => user.status === 'active');
      setAvailableUsers(users);
      
      // Validate existing GM assignments against actual users
      const validUserIds = new Set(users.map(user => user.id));
      setGmAssignments(prev => {
        const validated = {};
        Object.entries(prev).forEach(([teamId, userId]) => {
          if (validUserIds.has(userId)) {
            validated[teamId] = userId;
          }
        });
        return validated;
      });
      
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAllTeams = async () => {
    try {
      const response = await api.get('/teams');
      setAllTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchCurrentPick = async () => {
    try {
      const response = await api.get(`/fantasy-draft/${fantasyDraft.id}/current-pick`);
      setCurrentPick(response.data.currentPick);
    } catch (error) {
      console.error('Error fetching current pick:', error);
    }
  };

  const fetchAvailablePlayers = async () => {
    try {
      const response = await api.get(`/fantasy-draft/${fantasyDraft.id}/available-players`);
      setAvailablePlayers(response.data.availablePlayers);
    } catch (error) {
      console.error('Error fetching available players:', error);
    }
  };

  const fetchDraftPicks = async () => {
    try {
      const response = await api.get(`/fantasy-draft/${fantasyDraft.id}/picks`);
      setDraftPicks(response.data.picks);
    } catch (error) {
      console.error('Error fetching draft picks:', error);
    }
  };

  const fetchTeamRosters = async () => {
    try {
      const response = await api.get(`/fantasy-draft/${fantasyDraft.id}/rosters`);
      setTeamRosters(response.data.teams);
    } catch (error) {
      console.error('Error fetching team rosters:', error);
    }
  };

  const fetchDraftOrder = async () => {
    try {
      const response = await api.get(`/fantasy-draft/${fantasyDraft.id}/draft-order`);
      setDraftOrder(response.data.orderedTeams);
      return response.data;
    } catch (error) {
      console.error('Error fetching draft order:', error);
    }
  };

  const runLottery = async () => {
    try {
      setLoading(true);
      const response = await api.post(`/fantasy-draft/${fantasyDraft.id}/lottery`);
      await fetchFantasyDraft();
      await fetchDraftOrder();
      alert(`🎰 Lottery completed! ${response.data.lotteryResults.finalOrder[0].name} gets the first pick!`);
    } catch (error) {
      console.error('Error running lottery:', error);
      alert('Error running lottery: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const updateDraftOrder = async (newOrder) => {
    try {
      const teamIds = newOrder.map(team => team.id);
      await api.put(`/fantasy-draft/${fantasyDraft.id}/draft-order`, { teamOrder: teamIds });
      await fetchFantasyDraft();
      await fetchDraftOrder();
    } catch (error) {
      console.error('Error updating draft order:', error);
      alert('Error updating draft order: ' + (error.response?.data?.message || error.message));
    }
  };

  const createManualOrder = async () => {
    try {
      setLoading(true);
      
      // Get all teams with GMs assigned
      const teamsWithGMs = Object.keys(gmAssignments).map(teamId => {
        const team = allTeams.find(t => t.id === parseInt(teamId));
        const user = availableUsers.find(u => u.id === parseInt(gmAssignments[teamId]));
        return {
          ...team,
          owner: user
        };
      });

      // Create initial order (alphabetically by team name)
      const initialOrder = teamsWithGMs.sort((a, b) => 
        `${a.city} ${a.name}`.localeCompare(`${b.city} ${b.name}`)
      );

      // Set this as the draft order (without calling the lottery API)
      const teamIds = initialOrder.map(team => team.id);
      await api.put(`/fantasy-draft/${fantasyDraft.id}/draft-order`, { 
        teamOrder: teamIds,
        method: 'manual' 
      });
      
      await fetchFantasyDraft();
      await fetchDraftOrder();
      
      alert('✅ Manual draft order created! Now drag teams to arrange them according to your lottery results.');
      
    } catch (error) {
      console.error('Error creating manual order:', error);
      alert('Error creating manual order: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e, team) => {
    setIsDragging(true);
    setDraggedItem(team);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (!draggedItem) return;

    const dragIndex = draftOrder.findIndex(team => team.id === draggedItem.id);
    if (dragIndex === dropIndex) return;

    const newOrder = [...draftOrder];
    newOrder.splice(dragIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);
    
    setDraftOrder(newOrder);
    updateDraftOrder(newOrder);
  };

  const createFantasyDraft = async () => {
    try {
      const draftData = {
        seasonId: parseInt(seasonId),
        name: `Season ${new Date().getFullYear()} Fantasy Draft`,
        totalRounds: 13,
        draftOrder: 'standard',
        participatingTeamIds: [],
        autoPick: false
      };

      await api.post('/fantasy-draft', draftData);
      await fetchFantasyDraft();
      alert('Fantasy draft created successfully!');
    } catch (error) {
      console.error('Error creating fantasy draft:', error);
      alert('Error creating fantasy draft: ' + (error.response?.data?.message || error.message));
    }
  };

  const assignGM = async (teamId, userId) => {
    try {
      
      // Check if user is already assigned to another team for this fantasy draft
      const currentTeamId = Object.keys(gmAssignments).find(tId => gmAssignments[tId] === parseInt(userId));
      
      if (currentTeamId && currentTeamId !== teamId.toString()) {
        // Remove user from previous team assignment
        setGmAssignments(prev => {
          const updated = { ...prev };
          delete updated[currentTeamId];
          return updated;
        });
      }
      
      // Make API call to assign team
      const response = await api.put(`/users/${userId}/assign-team`, { teamId: parseInt(teamId) });
      
      // Update local state
      setGmAssignments(prev => ({
        ...prev,
        [teamId]: parseInt(userId)
      }));
      
      // Refresh the fantasy draft data
      await fetchFantasyDraft();
      
    } catch (error) {
      console.error('Error assigning GM:', error);
      alert('Error assigning GM: ' + (error.response?.data?.message || error.message));
    }
  };

  const removeGM = async (teamId, userId) => {
    try {
      
      // Check if user actually exists in availableUsers first (prevent known stale data)
      const userExists = availableUsers.some(user => user.id === parseInt(userId));
      if (!userExists) {
        setGmAssignments(prev => {
          const updated = { ...prev };
          delete updated[teamId];
          return updated;
        });
        await fetchFantasyDraft(); // Refresh data
        alert(`User no longer exists. GM assignment has been cleared.`);
        return;
      }
      
      // Make API call to remove team assignment
      const response = await api.put(`/users/${userId}/remove-team`);
      
      // Update local state - remove the team assignment
      setGmAssignments(prev => {
        const updated = { ...prev };
        delete updated[teamId];
        return updated;
      });
      
      // Refresh the fantasy draft data
      await fetchFantasyDraft();
      
    } catch (error) {
      console.error('Error removing GM:', error);
      
      // Handle different error types
      if (error.response?.status === 404) {
        // User doesn't exist (stale data) - clean up the UI
        setGmAssignments(prev => {
          const updated = { ...prev };
          delete updated[teamId];
          return updated;
        });
        await fetchFantasyDraft(); // Refresh to get current data
        alert(`User no longer exists. GM assignment has been cleared.`);
      } else if (error.response?.status === 400) {
        // User not assigned to team
        setGmAssignments(prev => {
          const updated = { ...prev };
          delete updated[teamId];
          return updated;
        });
        alert(`User is not assigned to any team. Assignment cleared.`);
      } else if (error.response?.status === 500) {
        // 500 error - likely stale user data
        setGmAssignments(prev => {
          const updated = { ...prev };
          delete updated[teamId];
          return updated;
        });
        await fetchFantasyDraft(); // Refresh to get current data
        alert(`Server error (likely non-existent user). GM assignment has been cleared.`);
      } else {
        // Other errors
        const errorMsg = error.response?.data?.message || error.message;
        alert('Error removing GM: ' + errorMsg);
      }
    }
  };

  const updateDraftSettings = async (settings) => {
    try {
      const teamsWithGMs = Object.keys(gmAssignments).map(teamId => parseInt(teamId));
      
      
      const updatedSettings = {
        ...settings,
        participatingTeamIds: teamsWithGMs
      };

      await api.put(`/fantasy-draft/${fantasyDraft.id}/settings`, updatedSettings);
      await fetchFantasyDraft();
    } catch (error) {
      console.error('Error updating draft settings:', error);
      alert('Error updating draft settings: ' + (error.response?.data?.message || error.message));
    }
  };

  const startDraft = async () => {
    try {
      await api.post(`/fantasy-draft/${fantasyDraft.id}/start`);
      await fetchFantasyDraft();
      setView('draft');
      alert('Fantasy draft started!');
    } catch (error) {
      console.error('Error starting draft:', error);
      alert('Error starting draft: ' + (error.response?.data?.message || error.message));
    }
  };

  const togglePause = async () => {
    try {
      await api.post(`/fantasy-draft/${fantasyDraft.id}/toggle-pause`);
      await fetchFantasyDraft();
    } catch (error) {
      console.error('Error toggling pause:', error);
      alert('Error toggling pause: ' + (error.response?.data?.message || error.message));
    }
  };

  const makePick = async () => {
    if (!selectedPlayer) {
      alert('Please select a player to draft');
      return;
    }

    try {
      await api.post(`/fantasy-draft/${fantasyDraft.id}/pick`, { playerId: selectedPlayer });
      setSelectedPlayer('');
      await fetchCurrentPick();
      await fetchAvailablePlayers();
      await fetchDraftPicks();
      await fetchFantasyDraft();
    } catch (error) {
      console.error('Error making pick:', error);
      alert('Error making pick: ' + (error.response?.data?.message || error.message));
    }
  };

  const filteredPlayers = availablePlayers.filter(player => {
    const matchesSearch = !searchTerm || 
      `${player.firstName} ${player.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.team?.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPosition = !positionFilter || 
      player.position.includes(positionFilter);
    
    return matchesSearch && matchesPosition;
  });

  const getStatusBadge = (status) => {
    const badges = {
      'not_started': 'bg-gray-50 dark:bg-gray-8000 text-white',
      'in_progress': 'bg-blue-50 dark:bg-blue-900/200 text-white',
      'paused': 'bg-yellow-500 text-black',
      'completed': 'bg-green-500 text-white'
    };
    return badges[status] || 'bg-gray-50 dark:bg-gray-8000 text-white';
  };

  if (loading) {
    return <div className="text-center py-12">Loading fantasy draft...</div>;
  }

  if (!fantasyDraft) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
          <h2 className="text-2xl font-bold mb-4">No Fantasy Draft Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            This season doesn't have a fantasy draft yet. Would you like to create one?
          </p>
          <button
            onClick={createFantasyDraft}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Create Fantasy Draft
          </button>
          <button
            onClick={() => navigate('/admin/seasons')}
            className="ml-4 bg-gray-300 text-gray-700 dark:text-gray-300 px-6 py-2 rounded-lg hover:bg-gray-400"
          >
            Back to Seasons
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{fantasyDraft.name}</h1>
            <div className="mt-2 space-x-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(fantasyDraft.status)}`}>
                {fantasyDraft.status.replace('_', ' ').toUpperCase()}
              </span>
              {fantasyDraft.status !== 'not_started' && (
                <span className="text-gray-600 dark:text-gray-400">
                  Round {fantasyDraft.currentRound} of {fantasyDraft.totalRounds}
                </span>
              )}
              {fantasyDraft.status === 'in_progress' && (
                <span className="text-green-600 dark:text-green-400 text-sm font-medium">
                  🔴 LIVE - Auto-updating every 5s
                </span>
              )}
            </div>
          </div>
          <div className="flex space-x-4">
            {fantasyDraft.status === 'in_progress' && (
              <button
                onClick={togglePause}
                className="bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-yellow-600"
              >
                ⏸️ Pause Draft
              </button>
            )}
            {fantasyDraft.status === 'paused' && (
              <button
                onClick={togglePause}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
              >
                ▶️ Resume Draft
              </button>
            )}
            <button
              onClick={() => navigate('/admin/seasons')}
              className="bg-gray-300 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400"
            >
              Back to Seasons
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setView('setup')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                view === 'setup'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              Setup & GMs
            </button>
            <button
              onClick={() => setView('draft')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                view === 'draft'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
              disabled={fantasyDraft.status === 'not_started'}
            >
              Draft Control
            </button>
            <button
              onClick={() => setView('rosters')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                view === 'rosters'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
              disabled={fantasyDraft.status === 'not_started'}
            >
              Team Rosters
            </button>
            <button
              onClick={() => window.open(`/draft/${fantasyDraft.id}`, '_blank')}
              className="py-4 px-1 border-b-2 border-transparent font-medium text-sm text-green-600 dark:text-green-400 hover:text-green-800"
            >
              🔗 Public Draft Board
            </button>
          </nav>
        </div>
      </div>

      {/* Content based on selected view */}
      {view === 'setup' && (
        <SetupView
          fantasyDraft={fantasyDraft}
          participatingTeams={participatingTeams}
          availableUsers={availableUsers}
          gmAssignments={gmAssignments}
          assignGM={assignGM}
          removeGM={removeGM}
          updateDraftSettings={updateDraftSettings}
          startDraft={startDraft}
          draftOrder={draftOrder}
          runLottery={runLottery}
          createManualOrder={createManualOrder}
          updateDraftOrder={updateDraftOrder}
          handleDragStart={handleDragStart}
          handleDragEnd={handleDragEnd}
          handleDragOver={handleDragOver}
          handleDrop={handleDrop}
          isDragging={isDragging}
          loading={loading}
        />
      )}

      {view === 'draft' && (
        <DraftView 
          fantasyDraft={fantasyDraft}
          currentPick={currentPick}
          availablePlayers={filteredPlayers}
          draftPicks={draftPicks}
          selectedPlayer={selectedPlayer}
          setSelectedPlayer={setSelectedPlayer}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          positionFilter={positionFilter}
          setPositionFilter={setPositionFilter}
          makePick={makePick}
        />
      )}

      {view === 'rosters' && (
        <RostersView teamRosters={teamRosters} />
      )}
    </div>
  );
};

// Setup View Component
const SetupView = ({ 
  fantasyDraft, participatingTeams, availableUsers, gmAssignments, assignGM, removeGM,
  updateDraftSettings, startDraft, draftOrder, runLottery, createManualOrder, updateDraftOrder,
  handleDragStart, handleDragEnd, handleDragOver, handleDrop, isDragging, loading
}) => {
  const [allTeams, setAllTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState(fantasyDraft.participatingTeams || []);

  useEffect(() => {
    fetchAllTeams();
  }, []);

  const fetchAllTeams = async () => {
    try {
      const response = await api.get('/teams');
      setAllTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const handleStartDraft = () => {
    if (!fantasyDraft.draftOrderSet) {
      alert('Please set the draft order first using the lottery or manual ordering');
      return;
    }
    
    const teamsWithGMs = Object.keys(gmAssignments).filter(teamId => gmAssignments[teamId]);
    
    if (teamsWithGMs.length < 2) {
      alert('At least 2 teams with assigned GMs are required to start the draft');
      return;
    }
    
    if (window.confirm(`Start the fantasy draft with ${teamsWithGMs.length} teams?`)) {
      startDraft();
    }
  };

  return (
    <div className="space-y-6">
      {/* Draft Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Draft Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total Rounds</label>
            <div className="text-lg font-semibold">{fantasyDraft.totalRounds}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Draft Order</label>
            <div className="text-lg font-semibold capitalize">{fantasyDraft.draftOrder}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Participating Teams</label>
            <div className="text-lg font-semibold">
              {Object.keys(gmAssignments).length} Teams Ready
            </div>
          </div>
        </div>
      </div>

      {/* GM Assignments */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">GM Assignments</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allTeams.map(team => {
            const assignedGmId = gmAssignments[team.id];
            const assignedUser = assignedGmId ? availableUsers.find(user => user.id === assignedGmId) : null;
            
            return (
              <div key={team.id} className="border rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <div className="w-3 h-3 rounded-full mr-2" style={{backgroundColor: team.primaryColor || '#666'}}></div>
                  <span className="font-medium">{team.city} {team.name}</span>
                </div>
                
                {assignedUser ? (
                  // Show assigned GM with remove button
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-200">
                      <div>
                        <div className="font-medium text-green-800">
                          {assignedUser.firstName ? `${assignedUser.firstName} (${assignedUser.username})` : assignedUser.username}
                        </div>
                        <div className="text-sm text-green-600 dark:text-green-400">✓ GM Assigned</div>
                      </div>
                      <button
                        onClick={() => {
                          if (window.confirm(`Remove ${assignedUser.firstName || assignedUser.username} as GM of ${team.city} ${team.name}?`)) {
                            removeGM(team.id, assignedUser.id);
                          }
                        }}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 hover:bg-red-100 dark:bg-red-900/30 p-1 rounded"
                        title="Remove GM"
                      >
                        ✕
                      </button>
                    </div>
                    
                    {/* Optional: Change GM dropdown */}
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          assignGM(team.id, e.target.value);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                    >
                      <option value="">Change GM...</option>
                      {availableUsers.map(user => {
                        const isCurrentlyAssigned = Object.values(gmAssignments).includes(user.id);
                        const isThisAssignment = gmAssignments[team.id] === user.id;
                        const displayName = user.firstName ? `${user.firstName} (${user.username})` : user.username;
                        
                        if (isThisAssignment) return null; // Don't show current assignment in dropdown
                        
                        return (
                          <option key={user.id} value={user.id} disabled={isCurrentlyAssigned}>
                            {displayName} {isCurrentlyAssigned ? '(Already assigned)' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ) : (
                  // Show GM selection dropdown
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        assignGM(team.id, e.target.value);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                  >
                    <option value="">Select GM...</option>
                    {availableUsers.map(user => {
                      const isCurrentlyAssigned = Object.values(gmAssignments).includes(user.id);
                      const displayName = user.firstName ? `${user.firstName} (${user.username})` : user.username;
                      
                      return (
                        <option key={user.id} value={user.id} disabled={isCurrentlyAssigned}>
                          {displayName} {isCurrentlyAssigned ? '(Already assigned)' : ''}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Draft Order Management */}
      {Object.keys(gmAssignments).length >= 2 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold">Draft Order</h3>
            <div className="flex space-x-2">
              <button
                onClick={createManualOrder}
                disabled={loading || fantasyDraft.draftOrderSet}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? '⚙️ Creating...' : '📝 Manual Setup'}
              </button>
              <button
                onClick={runLottery}
                disabled={loading || fantasyDraft.draftOrderSet}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? '🎲 Running...' : '🎰 Random Lottery'}
              </button>
              {fantasyDraft.draftOrderSet && (
                <span className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800">
                  ✅ Order Set ({fantasyDraft.draftOrderMethod})
                </span>
              )}
            </div>
          </div>
          
          {fantasyDraft.draftLotteryResults && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-sm font-medium text-blue-800 mb-2">
                🎲 Lottery completed at {new Date(fantasyDraft.draftLotteryResults.timestamp).toLocaleString()}
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-400">
                Results are shown below in the final draft order.
              </div>
            </div>
          )}

          {draftOrder && draftOrder.length > 0 ? (
            <div className="space-y-2">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {fantasyDraft.draftOrderSet ? 
                  "Draft order is set. You can still adjust by dragging teams:" : 
                  "Drag teams to set draft order manually:"
                }
              </div>
              {draftOrder.map((team, index) => (
                <div
                  key={team.id}
                  draggable={!loading}
                  onDragStart={(e) => handleDragStart(e, team)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`
                    flex items-center justify-between p-3 border-2 border-dashed border-gray-200 dark:border-gray-700 
                    rounded-lg cursor-move transition-all
                    ${isDragging ? 'opacity-50' : 'hover:border-blue-300 hover:bg-blue-50 dark:bg-blue-900/20'}
                    ${!loading ? 'cursor-move' : 'cursor-not-allowed'}
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <span className="bg-blue-50 dark:bg-blue-900/200 text-white font-bold text-sm px-2 py-1 rounded">
                      #{index + 1}
                    </span>
                    <div className="w-4 h-4 rounded-full" style={{backgroundColor: team.primaryColor || '#666'}}></div>
                    <span className="font-medium">{team.city} {team.name}</span>
                    {team.owner && (
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        (GM: {team.owner.firstName || team.owner.username})
                      </span>
                    )}
                  </div>
                  <div className="text-gray-400">
                    ⋮⋮
                  </div>
                </div>
              ))}
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                💡 Tip: The team at the top gets the 1st pick, 2nd team gets 2nd pick, etc. 
                {fantasyDraft.draftOrder === 'snake' && " (Snake draft reverses order each round)"}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              {Object.keys(gmAssignments).length === 0 ? 
                "Assign GMs to teams first before setting draft order." :
                <div className="space-y-4">
                  <p>Choose how to set the draft order:</p>
                  <div className="text-sm text-left max-w-md mx-auto space-y-2">
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <strong>📝 Manual Setup:</strong> Creates an initial alphabetical order that you can drag and rearrange to match your external lottery results.
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <strong>🎰 Random Lottery:</strong> Randomly assigns draft positions to all teams.
                    </div>
                  </div>
                </div>
              }
            </div>
          )}
        </div>
      )}

      {/* Start Draft */}
      {fantasyDraft.status === 'not_started' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-green-800 mb-2">Ready to Start?</h3>
          <div className="space-y-2 mb-4">
            <div className="flex items-center space-x-2">
              <span className={`w-3 h-3 rounded-full ${Object.keys(gmAssignments).length >= 2 ? 'bg-green-500' : 'bg-gray-300'}`}></span>
              <span className={Object.keys(gmAssignments).length >= 2 ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}>
                Teams with GMs: {Object.keys(gmAssignments).length} {Object.keys(gmAssignments).length >= 2 ? '✓' : '(need at least 2)'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`w-3 h-3 rounded-full ${fantasyDraft.draftOrderSet ? 'bg-green-500' : 'bg-gray-300'}`}></span>
              <span className={fantasyDraft.draftOrderSet ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}>
                Draft order: {fantasyDraft.draftOrderSet ? `Set (${fantasyDraft.draftOrderMethod}) ✓` : 'Not set (use lottery or manual ordering)'}
              </span>
            </div>
          </div>
          <button
            onClick={handleStartDraft}
            disabled={Object.keys(gmAssignments).length < 2 || !fantasyDraft.draftOrderSet}
            className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            🚀 Start Fantasy Draft
          </button>
          {(!fantasyDraft.draftOrderSet && Object.keys(gmAssignments).length >= 2) && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Set the draft order using the lottery or drag & drop above before starting.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Draft View Component
const DraftView = ({ 
  fantasyDraft, currentPick, availablePlayers, draftPicks, 
  selectedPlayer, setSelectedPlayer, searchTerm, setSearchTerm, 
  positionFilter, setPositionFilter, makePick 
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Current Pick */}
      <div className="lg:col-span-1">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Current Pick</h3>
          {currentPick ? (
            <div>
              <div className="text-center mb-4">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  Pick #{currentPick.overallPick}
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  Round {currentPick.round}, Pick {currentPick.pickNumber}
                </div>
              </div>
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="font-semibold text-lg">
                  {currentPick.team.city} {currentPick.team.name}
                </div>
                {currentPick.team.owner && (
                  <div className="text-gray-600 dark:text-gray-400">
                    GM: {currentPick.team.owner.firstName || currentPick.team.owner.username}
                  </div>
                )}
              </div>
              
              {/* Make Pick Section */}
              {fantasyDraft.status === 'in_progress' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Player to Draft
                  </label>
                  <select
                    value={selectedPlayer}
                    onChange={(e) => setSelectedPlayer(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg mb-3"
                  >
                    <option value="">Choose player...</option>
                    {availablePlayers.slice(0, 50).map(player => (
                      <option key={player.id} value={player.id}>
                        {player.firstName} {player.lastName} ({player.overallRating} OVR, {player.position})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={makePick}
                    disabled={!selectedPlayer}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    Make Pick 🎯
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 dark:text-gray-400">
              {fantasyDraft.status === 'completed' ? 'Draft Complete! 🏆' : 'No current pick available'}
            </div>
          )}
        </div>
      </div>

      {/* Available Players */}
      <div className="lg:col-span-2">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold">Available Players ({availablePlayers.length})</h3>
          </div>
          
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              placeholder="Search players or teams..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
            />
            <select
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
            >
              <option value="">All Positions</option>
              <option value="PG">Point Guard</option>
              <option value="SG">Shooting Guard</option>
              <option value="SF">Small Forward</option>
              <option value="PF">Power Forward</option>
              <option value="C">Center</option>
            </select>
          </div>

          {/* Players List */}
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left">Player</th>
                  <th className="px-4 py-2 text-left">Position</th>
                  <th className="px-4 py-2 text-left">OVR</th>
                  <th className="px-4 py-2 text-left">Current Team</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {availablePlayers.slice(0, 100).map(player => (
                  <tr 
                    key={player.id} 
                    className={`hover:bg-gray-50 dark:bg-gray-800 cursor-pointer ${
                      selectedPlayer === player.id.toString() ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                    onClick={() => setSelectedPlayer(player.id.toString())}
                  >
                    <td className="px-4 py-2 font-medium">
                      {player.firstName} {player.lastName}
                    </td>
                    <td className="px-4 py-2">{player.position}</td>
                    <td className="px-4 py-2 font-semibold">{player.overallRating}</td>
                    <td className="px-4 py-2">
                      {player.team ? `${player.team.city} ${player.team.name}` : 'Free Agent'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recent Picks */}
      <div className="lg:col-span-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Recent Picks</h3>
          <div className="max-h-64 overflow-y-auto">
            <div className="space-y-2">
              {draftPicks
                .filter(pick => pick.playerId)
                .slice(-10)
                .reverse()
                .map(pick => (
                <div key={pick.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="bg-blue-50 dark:bg-blue-900/200 text-white text-xs px-2 py-1 rounded">
                      #{pick.overallPick}
                    </span>
                    <span className="font-medium">
                      {pick.player?.firstName} {pick.player?.lastName}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">({pick.player?.overallRating} OVR)</span>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{pick.team.city} {pick.team.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Round {pick.round}, Pick {pick.pickNumber}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Rosters View Component
const RostersView = ({ teamRosters }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {teamRosters.map(team => (
        <div key={team.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <div className="w-4 h-4 rounded-full mr-3" style={{backgroundColor: team.primaryColor || '#666'}}></div>
            <div>
              <h3 className="text-lg font-semibold">{team.city} {team.name}</h3>
              {team.owner && (
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  GM: {team.owner.firstName || team.owner.username}
                </div>
              )}
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Roster ({team.players?.length || 0} players)
            </div>
            {team.players?.map(player => (
              <div key={player.id} className="flex justify-between items-center py-1">
                <span className="text-sm">
                  {player.firstName} {player.lastName}
                </span>
                <div className="text-right">
                  <span className="text-sm font-medium">{player.overallRating}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">{player.position}</span>
                </div>
              </div>
            )) || (
              <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                No players drafted yet
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FantasyDraftManagement;